import React from "react";
import { Link, Head } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { CALENDLY_COST_REVIEW_URL } from "@/lib/urls";
import {
  BarChart2,
  Search,
  GitPullRequest,
  ShieldCheck,
  ChevronRight,
  Container,
  Activity,
  Calendar,
  RefreshCw,
  CheckCircle,
} from "lucide-react";
import Layout from "../Layout";
import { CallToAction } from "@/components/marketing/CallToAction";
import {
  LatestArticles,
  type Article,
} from "@/components/marketing/LatestArticles";
import {
  PullRequestPreview,
  InlineCode,
} from "@/components/marketing/PullRequestPreview";
import { DiffPreview, DiffLine } from "@/components/marketing/DiffPreview";
import { SlackMessagePreview } from "@/components/marketing/SlackMessagePreview";
import { ProductRecommendationsMockup } from "@/components/marketing/ProductRecommendationsMockup";
import { ProductHowItWorks } from "@/components/marketing/ProductHowItWorks";
import { ProductThresholds } from "@/components/marketing/ProductThresholds";
import { ProductGuardrails } from "@/components/marketing/ProductGuardrails";
import { FAQ, type FAQItem } from "@/components/marketing/FAQ";

// ── Hero ─────────────────────────────────────────────────────────────────────

const BREADCRUMB_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://infralyst.io",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "ECS Fargate Rightsizing",
      item: "https://infralyst.io/products/ecs-fargate",
    },
  ],
});

const SOFTWARE_APP_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Infralyst ECS Fargate Optimization",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Infralyst analyzes real CloudWatch utilization for your ECS Fargate services and generates ready-to-review Terraform PRs to cut ECS Fargate costs.",
  url: "https://infralyst.io/products/ecs-fargate",
  offers: {
    "@type": "AggregateOffer",
    lowPrice: "0",
    highPrice: "99",
    priceCurrency: "USD",
    offerCount: 2,
    offers: [
      {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        description: "Free plan with 3 PRs per workspace",
      },
      {
        "@type": "Offer",
        price: "99",
        priceCurrency: "USD",
        description: "Pro plan with unlimited PRs per workspace, billed monthly",
      },
    ],
  },
  publisher: {
    "@type": "Organization",
    name: "Infralyst",
    url: "https://infralyst.io",
  },
});

const RECOMMENDATIONS = [
  {
    name: "api-service",
    subtext: "us-east-1 · 84 days",
    current: "1vCPU-2GB",
    recommended: "0.5vCPU-1GB",
    cpuAvg: "3.8%",
    cpuPct: "17%",
    memAvg: "14%",
    memPct: "26%",
    tasks: "2",
    savings: "$41/mo",
    days: 84,
    region: "us-east-1",
  },
  {
    name: "worker-processor",
    subtext: "us-east-1 · 63 days",
    current: "2vCPU-4GB",
    recommended: "1vCPU-2GB",
    cpuAvg: "6.2%",
    cpuPct: "22%",
    memAvg: "11%",
    memPct: "29%",
    tasks: "3",
    savings: "$58/mo",
    days: 63,
    region: "us-east-1",
  },
  {
    name: "cache-sidecar",
    subtext: "us-east-1 · 47 days",
    current: "0.5vCPU-2GB",
    recommended: "0.25vCPU-1GB",
    cpuAvg: "4.1%",
    cpuPct: "18%",
    memAvg: "9%",
    memPct: "22%",
    tasks: "1",
    savings: "$19/mo",
    days: 47,
    region: "us-east-1",
  },
];

function Hero(): React.ReactElement {
  return (
    <>
      <Head>
        <script type="application/ld+json">{BREADCRUMB_JSON_LD}</script>
        <script type="application/ld+json">{SOFTWARE_APP_JSON_LD}</script>
      </Head>
      <section className="px-4 pt-28 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-12 items-center">
            {/* Left: text */}
            <div>
              {/* Breadcrumb */}
              <nav
                aria-label="Breadcrumb"
                className="flex items-center gap-1 text-sm text-surface-foreground/50 mb-6"
              >
                <span>Products</span>
                <ChevronRight className="size-3.5" />
                <span className="flex items-center gap-1 text-foreground font-medium">
                  <Container className="size-3.5" />
                  ECS Fargate
                </span>
              </nav>

              <h1 className="mb-5 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
                ECS Fargate <span className="text-primary">Rightsizing</span>
              </h1>
              <p className="mb-8 text-xl leading-relaxed text-surface-foreground/80">
                Infralyst analyzes CloudWatch utilization for each ECS Fargate
                service and surfaces savings opportunities. When you're ready,
                generate ready to merge Terraform PRs from Slack or the
                dashboard.
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <Button asChild size="lg">
                  <Link href="/signup">Start free with 3 PRs</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <a href={CALENDLY_COST_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                    Book a demo
                  </a>
                </Button>
              </div>
              <ul className="flex flex-col gap-2">
                {["Unlimited free recommendations", "Read-only IAM role", "Your team reviews and merges every change"].map((point) => (
                  <li key={point} className="flex items-center gap-2 text-sm text-surface-foreground/60">
                    <CheckCircle className="size-4 text-green-500 flex-none" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: UI mockup */}
            <div className="lg:pl-4">
              <ProductRecommendationsMockup
                title="ECS Fargate Recommendations"
                icon={Container}
                recommendations={RECOMMENDATIONS}
                footerText="Analyzed 12 services across 2 regions"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ── The Problem ───────────────────────────────────────────────────────────────

function TheProblem(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-4xl font-bold mb-6">
          You're paying for Fargate capacity
          <br />
          <span className="text-primary">you don't use</span>
        </h2>
        <p className="text-lg leading-relaxed text-surface-foreground/80 mb-4">
          Teams pick CPU and memory allocations when they first define a task
          and never revisit them. Fargate bills per vCPU-hour and GB-hour, so
          every unit of unused capacity is a direct cost. The problem compounds
          quietly across dozens of services.
        </p>
        <p className="text-lg leading-relaxed text-surface-foreground/80">
          AWS Cost Explorer can flag the waste, but it stops at a
          recommendation. Infralyst picks up where it leaves off: it analyzes
          real CloudWatch utilization at the service level, selects a
          conservative target configuration, and generates a PR your team can
          review and merge.
        </p>
      </div>
    </section>
  );
}

// ── How the Analysis Works ────────────────────────────────────────────────────

const ANALYSIS_STEPS = [
  {
    icon: BarChart2,
    title: "1. Collect utilization metrics",
    description:
      "Infralyst pulls CPU and memory utilization from CloudWatch for each ECS Fargate service, using up to 365 days of historical data.",
  },
  {
    icon: Search,
    title: "2. Run the analysis gates",
    description:
      "Each service passes through data quality validation, utilization thresholds, and seasonality checks. A service only gets a recommendation if all gates pass.",
  },
  {
    icon: GitPullRequest,
    title: "3. Generate the recommendation",
    description:
      "Infralyst recommends the next smaller CPU/memory configuration. When you're ready, an AI agent reads your Terraform, writes the change, and validates it. The analysis is fully deterministic.",
  },
] as const;

// ── Thresholds & Methodology ──────────────────────────────────────────────────

const GATES = [
  {
    gate: "Pre-flight",
    icon: ShieldCheck,
    checks: [
      {
        metric: "Shared task definition",
        threshold: "Not shared across multiple services",
      },
      {
        metric: "Minimum size",
        threshold: "Not already smallest Fargate configuration",
      },
    ],
  },
  {
    gate: "Data quality",
    icon: BarChart2,
    checks: [
      { metric: "Data window", threshold: "≥ 30 days of metric data" },
      { metric: "Uptime coverage", threshold: "≥ 95% within the data window" },
    ],
  },
  {
    gate: "CPU & Memory",
    icon: Activity,
    checks: [
      { metric: "P99.5 · 1–60 days (5 min)", threshold: "< 40%" },
      { metric: "P95 · 60–365 days (hourly)", threshold: "< 40%" },
    ],
  },
  {
    gate: "Seasonality",
    icon: Calendar,
    description:
      "For services with 60–365 days of data, P95 is checked per weekly bucket. If any week exceeds the 40% threshold, the service is excluded.",
    checks: [],
  },
] as const;

// ── Trigger PRs from Slack ────────────────────────────────────────────────────

const SLACK_REC = RECOMMENDATIONS[0];

function TriggerFromSlack(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          Trigger PRs{" "}
          <span className="text-primary">directly from Slack</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-10 max-w-2xl mx-auto">
          Get notified when new savings are found. Review the details and
          generate a PR without leaving Slack.
        </p>

        <div className="max-w-xl mx-auto overflow-x-auto">
          <div className="min-w-[500px]">
            <SlackMessagePreview>
              {/* Header */}
              <p className="font-bold text-[15px] leading-snug mb-5">
                New ECS Fargate savings recommendation · Save $41.00/mo
              </p>

              {/* All fields in one unified grid */}
              <div className="grid grid-cols-2 gap-x-10 mb-1">
                <div className="mb-4">
                  <p className="font-bold text-sm">Resource</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.name} ({SLACK_REC.region})
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Lookback</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.days}d
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">CPU Avg</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.cpuAvg}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">CPU P99.5</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.cpuPct}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Mem Avg</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.memAvg}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Mem P99.5</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.memPct}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Current</p>
                  <span className="font-mono text-sm text-surface-foreground/60">
                    {SLACK_REC.current}
                  </span>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Recommended</p>
                  <span className="font-mono text-sm text-primary font-medium">
                    {SLACK_REC.recommended}
                  </span>
                </div>
              </div>

              {/* Link */}
              <p className="text-xs text-primary mb-3">View on Infralyst</p>

              {/* Buttons */}
              <div className="flex items-center gap-2">
                <button className="text-xs font-bold px-3 py-1.5 rounded-md bg-primary text-primary-foreground">
                  Generate PR
                </button>
                <button className="text-xs font-bold px-3 py-1.5 rounded-md border border-foreground/25 text-foreground/70">
                  Dismiss
                </button>
              </div>
            </SlackMessagePreview>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── What the PR Looks Like ────────────────────────────────────────────────────

const FARGATE_PR_BODY = (
  <>
    <p>
      ECS Fargate service <InlineCode>api-service</InlineCode> in{" "}
      <InlineCode>us-east-1</InlineCode> can be downsized from{" "}
      <InlineCode>1vCPU-2GB</InlineCode> to{" "}
      <InlineCode>0.5vCPU-1GB</InlineCode>, saving an estimated $41/mo.
    </p>

    <table className="w-full text-sm border-collapse mb-4">
      <thead>
        <tr className="border-b border-foreground/20">
          <th className="text-left py-1 pr-4">Metric</th>
          <th className="text-left py-1 pr-4">Avg</th>
          <th className="text-left py-1">P99.5</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-b border-foreground/10">
          <td className="py-1 pr-4">CPU</td>
          <td className="py-1 pr-4">3.8%</td>
          <td className="py-1">17%</td>
        </tr>
        <tr>
          <td className="py-1 pr-4">Memory</td>
          <td className="py-1 pr-4">14%</td>
          <td className="py-1">26%</td>
        </tr>
      </tbody>
    </table>

    <p className="text-sm text-foreground/70">Lookback: 84 days</p>

    <p>
      <a
        href="https://infralyst.io/workspaces/acme-corp/aws_accounts/mock-uuid"
        className="text-primary underline"
      >
        View on Infralyst
      </a>
    </p>
  </>
);

const FARGATE_DIFF_LINES: DiffLine[] = [
  {
    type: "hunk",
    content:
      '@@ -8,14 +8,14 @@ resource "aws_ecs_task_definition" "api_service" {',
  },
  {
    type: "context",
    content: 'resource "aws_ecs_task_definition" "api_service" {',
    oldLineNumber: 8,
    newLineNumber: 8,
  },
  {
    type: "context",
    content: '  family                   = "api-service"',
    oldLineNumber: 9,
    newLineNumber: 9,
  },
  {
    type: "context",
    content: '  requires_compatibilities = ["FARGATE"]',
    oldLineNumber: 10,
    newLineNumber: 10,
  },
  {
    type: "context",
    content: '  network_mode             = "awsvpc"',
    oldLineNumber: 11,
    newLineNumber: 11,
  },
  {
    type: "removed",
    content: '  cpu                      = "1024"',
    oldLineNumber: 12,
  },
  {
    type: "added",
    content: '  cpu                      = "512"',
    newLineNumber: 12,
  },
  {
    type: "removed",
    content: '  memory                   = "2048"',
    oldLineNumber: 13,
  },
  {
    type: "added",
    content: '  memory                   = "1024"',
    newLineNumber: 13,
  },
  {
    type: "context",
    content: "  execution_role_arn       = aws_iam_role.ecs_task_execution.arn",
    oldLineNumber: 14,
    newLineNumber: 14,
  },
  {
    type: "context",
    content: "}",
    oldLineNumber: 15,
    newLineNumber: 15,
  },
  {
    type: "hunk",
    content:
      '@@ -28,6 +28,12 @@ resource "aws_ecs_service" "api_service" {',
  },
  {
    type: "context",
    content: '  task_definition = aws_ecs_task_definition.api_service.arn',
    oldLineNumber: 28,
    newLineNumber: 28,
  },
  {
    type: "context",
    content: "  desired_count   = 2",
    oldLineNumber: 29,
    newLineNumber: 29,
  },
  {
    type: "added",
    content: "  deployment_configuration {",
    newLineNumber: 30,
  },
  {
    type: "added",
    content: "    maximum_percent         = 200",
    newLineNumber: 31,
  },
  {
    type: "added",
    content: "    minimum_healthy_percent = 50",
    newLineNumber: 32,
  },
  {
    type: "added",
    content: "  }",
    newLineNumber: 33,
  },
  {
    type: "context",
    content: "}",
    oldLineNumber: 30,
    newLineNumber: 34,
  },
];

function WhatThePrLooksLike(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          What the <span className="text-primary">PR looks like</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-10 max-w-2xl mx-auto">
          Infralyst opens a Terraform pull request in your repo. Your team
          reviews and merges it like any other change.
        </p>

        <PullRequestPreview
          title="Downsize ECS Fargate service api-service: 1vCPU-2GB → 0.5vCPU-1GB (-$41/mo)"
          author="infralyst"
          status="open"
          body={FARGATE_PR_BODY}
          filesChangedContent={
            <DiffPreview filename="api_service.tf" lines={FARGATE_DIFF_LINES} />
          }
        />
      </div>
    </section>
  );
}

// ── Safety & Guardrails ───────────────────────────────────────────────────────

const GUARDRAILS = [
  {
    icon: ShieldCheck,
    title: "Conservative thresholds",
    description:
      "Infralyst won't recommend rightsizing unless CPU and memory P99.5 (or P95 for longer windows) are comfortably below 40%. A single spike won't trigger a false recommendation.",
  },
  {
    icon: Calendar,
    title: "Seasonality-aware",
    description:
      "For services with 60+ days of data, P95 utilization is checked per weekly bucket over the full observation window. If any week exceeds 40%, the service is excluded. No recommendation, no risk.",
  },
  {
    icon: RefreshCw,
    title: "Zero downtime deployment",
    description:
      "Every PR includes a deployment configuration block. ECS rolls out the new task definition gradually, keeping your service available throughout.",
  },
  {
    icon: GitPullRequest,
    title: "PR-only changes",
    description:
      "Infralyst never applies changes directly. Every recommendation is delivered as a notification in Slack or in our console. You trigger PR generation, review, test, and merge on your timeline.",
  },
] as const;

// ── FAQ ───────────────────────────────────────────────────────────────────────

const FARGATE_FAQ_ITEMS: readonly FAQItem[] = [
  {
    question: "How do you optimize ECS Fargate task CPU and memory settings?",
    answer:
      "Pull CPU and memory utilization from CloudWatch over at least 30 days. If the P99.5 is well below the allocated CPU and memory, the task definition is a candidate for downsizing to the next smaller Fargate configuration. Infralyst automates this analysis across all your Fargate services and generates a Terraform PR with the exact cpu and memory values to change.",
  },
  {
    question: "What's the difference between rightsizing ECS Fargate and EC2 instances?",
    answer:
      "Fargate rightsizing changes the CPU and memory allocation on a task definition. ECS rolls out the new configuration with zero downtime using a rolling deployment. EC2 rightsizing changes the instance type, which requires a stop and start for standalone instances. Infralyst handles both, with deployment configuration included in every Fargate PR.",
  },
  {
    question: "What thresholds does Infralyst use before recommending a Fargate rightsize?",
    answer:
      "For services with 1 to 60 days of data, CPU and memory P99.5 must stay below 40% on 5-minute CloudWatch metrics. For 60 to 365 days, Infralyst checks P95 on hourly metrics and runs a seasonality analysis: P95 per weekly bucket across the full window. If any single week exceeds 40%, the service is excluded from recommendations.",
  },
  {
    question: "How much CloudWatch data does Infralyst need before recommending a Fargate resize?",
    answer:
      "At least 30 days, with 95% or more uptime coverage within that window. Short observation windows don't capture enough workload variation to distinguish real utilization from a quiet period. Infralyst won't suggest a downsize based on a quiet week.",
  },
  {
    question: "Which ECS Fargate services does Infralyst skip?",
    answer:
      "Three cases: services already running the smallest Fargate configuration, services that share a task definition across multiple ECS services, and services without enough CloudWatch history or uptime coverage to pass the data quality gate. Infralyst won't recommend a change when the data doesn't support it.",
  },
  {
    question: "How does Infralyst generate the Terraform PR for a Fargate resize?",
    answer:
      "An AI agent reads your Terraform files, locates the cpu and memory arguments on the target task definition, writes the updated values, and adds a deployment configuration block for zero downtime rollout. The recommendation itself (which service to resize and what size to use) is fully deterministic. The AI handles only the file editing step.",
  },
  {
    question: "Is it safe to downsize ECS Fargate services in production?",
    answer:
      "Infralyst only recommends a downsize when CPU and memory P99.5 stay below 40% (or P95 for longer windows), including seasonality checks across weekly buckets. Every PR includes a deployment configuration block so ECS rolls out the change gradually. Your team reviews and merges the PR, so nothing changes until you're ready.",
  },
  {
    question: "What Fargate CPU and memory configurations are available?",
    answer:
      "Fargate has fixed CPU/memory combinations (e.g., 0.25 vCPU with 0.5-2 GB, 0.5 vCPU with 1-4 GB, up to 16 vCPU with 32-120 GB). Infralyst recommends the next smaller valid configuration that fits your workload. It won't suggest an invalid combination or skip over intermediate sizes.",
  },
  {
    question: "What happens if a Fargate resize causes performance issues after the PR is merged?",
    answer:
      "Infralyst doesn't merge PRs. Your team does, after reviewing the utilization data in the PR body. If the new configuration doesn't work, reverting is a standard PR that changes two values (cpu and memory) on the task definition. ECS rolls the revert out with zero downtime, same as the original change.",
  },
  {
    question: "Does Infralyst handle Fargate services that share a task definition?",
    answer:
      "Infralyst detects when multiple ECS services reference the same task definition and skips the recommendation. Changing a shared task definition would affect all services using it, which could cause unintended downsizing. Each service needs its own task definition for Infralyst to generate a safe, isolated PR.",
  },
] as const;

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Fargate({
  articles,
}: {
  articles: Article[];
}): React.ReactElement {
  return (
    <Layout
      title="AWS ECS Fargate Cost Optimization"
      description="Infralyst analyzes real CloudWatch utilization for your ECS Fargate services and generates ready-to-review Terraform PRs to cut ECS Fargate costs."
    >
      <Hero />
      <TheProblem />
      <ProductHowItWorks steps={ANALYSIS_STEPS} />
      <ProductThresholds
        subtitle="Every gate and threshold Infralyst checks before recommending Fargate rightsizing. These are intentionally conservative. We'd rather miss a savings opportunity than recommend a change that causes problems."
        gates={GATES}
      />
      <WhatThePrLooksLike />
      <TriggerFromSlack />
      <ProductGuardrails
        guardrails={GUARDRAILS}
        crossLink={
          <>
            Also available for{" "}
            <Link href="/products/ec2" className="text-primary hover:underline">
              EC2 Instances
            </Link>
            ,{" "}
            <Link
              href="/products/auto-scaling-groups"
              className="text-primary hover:underline"
            >
              Auto Scaling Groups
            </Link>
            , and{" "}
            <Link href="/products/ebs" className="text-primary hover:underline">
              EBS Volumes
            </Link>
            .
          </>
        }
      />
      <FAQ
        items={FARGATE_FAQ_ITEMS}
        title="ECS Fargate Rightsizing FAQ"
        highlightWord="Rightsizing"
        className="py-16 px-4"
      />
      <LatestArticles
        articles={articles}
        title={
          <>
            Learn more about ECS Fargate{" "}
            <span className="text-primary">optimization</span>
          </>
        }
        subtitle={null}
      />
      <CallToAction
        heading="Start rightsizing your ECS Fargate services"
        subheading="Connect your AWS account and Infralyst will find Fargate services worth rightsizing. Generate the Terraform PR in one click when you're ready."
      />
    </Layout>
  );
}
